import memoizeOne from 'memoize-one';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { Country } from '~/Api/Country';
import { ReferenceBasketVersion, type ReferencedProductType } from '~/Api/SiPriceReferencing/MaxPrice';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import {
	ErrorResponse,
	Product,
	ProductUpdate,
	SiMaxPrice as ApiSiMaxPrice,
	ValidationResult,
} from '~/Api/User/ProductGroup/SiMaxPrice';
import { handleError } from '~/ErrorHandler';
import { logGoogleTagManagerGroupProductsRemove } from '~/GoogleTagManager';
import { removeFromGroup } from '~/GroupProduct';
import {
	fetchReferenceBasketVersions,
	Module,
	pruneReferenceBasketVersions,
	selectReferenceBasketVersions,
	State as PriceReferencingState,
} from '~/PriceReferencing';
import { PricedProduct, Product as GeneralProduct } from '~/Product';
import { pruneDatasets } from '~/ProductDataset';
import { pruneAllPrices } from '~/ProductPrice';
import { ThunkAction, ThunkDispatch } from '~/Store';

import {
	commitSiMaxPriceUpdate,
	fetchReferenceProductTypes,
	fetchSiMaxPrice,
	handleCommitError,
	handleSiMaxPriceUpdateResponse,
	pruneSiMaxPrice,
	scheduleSiMaxPriceUpdate,
} from './actions';
import { ProductRow } from './ProductsTable/ProductsTable';
import { selectExport, selectSiMaxPrice, State as SiMaxPriceState } from './reducer';
import SiMaxPriceContent from './SiMaxPrice';

const containsCountry = (countries: Country[], code: string): boolean => {
	return countries.find((country) => country.alpha2 === code) !== undefined;
};

const compareReferences = (reference: ProductRow, otherReference: ProductRow): number => {
	if (reference.product && !otherReference.product) {
		return -1;
	}
	if (!reference.product && otherReference.product) {
		return 1;
	}
	const comparison = reference.country?.alpha2.localeCompare(otherReference.country?.alpha2 || '') || 0;
	if (comparison !== 0) {
		return comparison;
	}
	if (reference.product && reference.product.product && otherReference.product && otherReference.product.product) {
		return reference.product.product.brandName.localeCompare(otherReference.product.product.brandName);
	}
	return 0;
};

const groupProducts = memoizeOne(
	(
		siMaxPrice: ApiSiMaxPrice | null,
		versions: ReferenceBasketVersion[] | null
	): {
		pricedProducts: PricedProduct<Product>[];
		slovenianProducts: ProductRow[];
		referencedProducts: ProductRow[];
		otherEuEeaProducts: ProductRow[];
		otherEuropeProducts: ProductRow[];
		otherProducts: ProductRow[];
	} => {
		const version =
			versions !== null && siMaxPrice !== null
				? versions.find((version) => version.id === siMaxPrice.reference.referenceBasketVersion)
				: null;
		const referenceCountries = version?.referenceCountries ?? [];
		const otherEuEeaCountries = version?.otherEuEeaCountries ?? [];
		const otherEuropeCountries = version?.otherEuropeCountries ?? [];
		const products = siMaxPrice !== null && version !== null ? siMaxPrice.products : [];
		const nonsetCountries = new Set([
			...referenceCountries.map((country) => country.alpha2),
			...otherEuEeaCountries.map((country) => country.alpha2),
			...otherEuropeCountries.map((country) => country.alpha2),
		]);
		const usedForCalculationIds = new Set(
			siMaxPrice?.finalCalculation?.comparativePrice?.fromProducts?.map((product) => product.id)
		);

		const slovenianProducts: ProductRow[] = [];
		const referencedProducts: ProductRow[] = [];
		const otherEuEeaProducts: ProductRow[] = [];
		const otherEuropeProducts: ProductRow[] = [];
		const otherProducts: ProductRow[] = [];

		products.forEach((product) => {
			const { country } = product.product.dataset.source;
			if (country.alpha2 === 'SI') {
				slovenianProducts.push({
					country: null,
					product,
					usedForCalculation: usedForCalculationIds.has(product.product.id),
				});
				return;
			}

			if (containsCountry(referenceCountries, country.alpha2)) {
				referencedProducts.push({
					country,
					product,
					usedForCalculation: usedForCalculationIds.has(product.product.id),
				});
				nonsetCountries.delete(country.alpha2);
				return;
			}

			if (containsCountry(otherEuEeaCountries, country.alpha2)) {
				otherEuEeaProducts.push({
					country,
					product,
					usedForCalculation: usedForCalculationIds.has(product.product.id),
				});
				nonsetCountries.delete(country.alpha2);
				return;
			}

			if (containsCountry(otherEuropeCountries, country.alpha2)) {
				otherEuropeProducts.push({
					country,
					product,
					usedForCalculation: usedForCalculationIds.has(product.product.id),
				});
				nonsetCountries.delete(country.alpha2);
				return;
			}

			otherProducts.push({
				country: null,
				product,
				usedForCalculation: usedForCalculationIds.has(product.product.id),
			});
		});

		if (slovenianProducts.length === 0) {
			slovenianProducts.push({
				country: new Country('SI', 'Slovenian Republic'),
				product: null,
				usedForCalculation: false,
			});
		}

		referenceCountries.forEach((country) => {
			if (nonsetCountries.has(country.alpha2)) {
				referencedProducts.push({
					country,
					product: null,
					usedForCalculation: false,
				});
			}
		});

		if (otherEuEeaProducts.length > 0) {
			otherEuEeaCountries.forEach((country) => {
				if (nonsetCountries.has(country.alpha2)) {
					otherEuEeaProducts.push({
						country,
						product: null,
						usedForCalculation: false,
					});
				}
			});
		}

		if (otherEuropeProducts.length > 0) {
			otherEuropeCountries.forEach((country) => {
				if (nonsetCountries.has(country.alpha2)) {
					otherEuropeProducts.push({
						country,
						product: null,
						usedForCalculation: false,
					});
				}
			});
		}

		referencedProducts.sort(compareReferences);
		otherEuEeaProducts.sort(compareReferences);
		otherEuropeProducts.sort(compareReferences);

		const pricedProducts: PricedProduct<Product>[] = [
			...slovenianProducts,
			...referencedProducts,
			...otherEuEeaProducts,
			...otherEuropeProducts,
			...otherProducts,
		]
			.filter((product): product is ProductRow & { product: Product } => Boolean(product.product))
			.map((product) => new PricedProduct(product.product, product.product?.product, null));

		return {
			pricedProducts,
			slovenianProducts,
			referencedProducts,
			otherEuEeaProducts,
			otherEuropeProducts,
			otherProducts,
		};
	}
);

interface State {
	siMaxPrice: SiMaxPriceState;
	priceReferencing: PriceReferencingState;
}

function mapStateToProps(
	state: State,
	{ group }: { group: UserProductGroup }
): {
	pricedProducts: PricedProduct<Product>[];
	slovenianProducts: ProductRow[];
	otherProducts: ProductRow[];
	siMaxPrice: ApiSiMaxPrice | null;
	versions: ReferenceBasketVersion[] | null;
	validationResult: ValidationResult | null;
	isFetching: boolean;
} & ReturnType<typeof groupProducts> {
	const siMaxPriceState = selectSiMaxPrice(state.siMaxPrice, group.id);
	const siMaxPrice = siMaxPriceState?.siMaxPrice || null;
	const versions: ReferenceBasketVersion[] | null = selectReferenceBasketVersions(
		state.priceReferencing,
		Module.siMaxPrice
	);

	return {
		...groupProducts(siMaxPrice, versions),
		siMaxPrice,
		versions,
		validationResult: selectExport(state.siMaxPrice, group.id)?.validationResult || null,
		isFetching: Boolean(siMaxPriceState?.isFetching),
	};
}

const actions = {
	onFetch:
		(group: UserProductGroup): ThunkAction<Promise<void>, any> =>
		async (dispatch) => {
			dispatch(fetchReferenceBasketVersions(Module.siMaxPrice));
			dispatch(fetchReferenceProductTypes());

			try {
				await dispatch(commitSiMaxPriceUpdate(group.id));
			} catch (error) {
				dispatch(handleCommitError(error));
			}

			try {
				await dispatch(fetchSiMaxPrice(group.id));
			} catch (error) {
				if (!(error instanceof ErrorResponse)) {
					dispatch(handleError(error));
					return;
				}
				error.handleProductGroupNotFound();
				dispatch(handleError(error));
			}
		},
	onPrune:
		(group: UserProductGroup): ThunkAction<Promise<void>, any> =>
		async (dispatch: ThunkDispatch<AnyAction>) => {
			try {
				await dispatch(commitSiMaxPriceUpdate(group.id));
			} catch (error) {
				dispatch(handleCommitError(error));
			}
			dispatch(pruneSiMaxPrice(group.id));
			dispatch(pruneReferenceBasketVersions());
			dispatch(pruneAllPrices());
			dispatch(pruneDatasets());
		},
	onUpdate:
		(
			group: UserProductGroup,
			referenceBasketVersion: string,
			productType: string,
			packageSize: number | null,
			dosageStrength: number | null,
			historicProductDataViewDate: Date | null,
			products: ProductUpdate[]
		): ThunkAction<Promise<void>, any> =>
		async (dispatch) => {
			try {
				await dispatch(
					scheduleSiMaxPriceUpdate(
						group.id,
						referenceBasketVersion,
						productType,
						packageSize,
						dosageStrength,
						historicProductDataViewDate,
						products
					)
				);
			} catch (error) {
				dispatch(handleSiMaxPriceUpdateResponse(error, group.id));
			}
		},
	onSave:
		(group: UserProductGroup): ThunkAction<Promise<void>, any> =>
		async (dispatch) => {
			try {
				await dispatch(commitSiMaxPriceUpdate(group.id));
			} catch (error) {
				dispatch(handleSiMaxPriceUpdateResponse(error, group.id));
			}
		},
	onDeleteItems:
		(group: UserProductGroup, products: GeneralProduct[], onSuccess: () => void) =>
		async (dispatch: ThunkDispatch<AnyAction>) => {
			try {
				await dispatch(removeFromGroup(group.id, products));
				logGoogleTagManagerGroupProductsRemove(group.id, products);
				if (onSuccess !== undefined) {
					onSuccess();
				}
			} catch (error) {
				dispatch(handleError(error));
			}
		},
};

export default connect(mapStateToProps, actions)(SiMaxPriceContent);
